import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/articles";
import { getRelatedArticles, getCategoryName } from "@/utils/articles";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";

export default function MaterialDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(() => articles.find((item) => item.slug === slug), [slug]);
  const [copied, setCopied] = useState(false);

  useSeo({
    title: article
      ? `${article.title}｜看料88 热点话题`
      : "话题不存在｜看料88",
    description: article
      ? article.excerpt.slice(0, 150)
      : "该话题可能已经下线或链接有误，请返回看料88 猛料合集浏览最新话题。",
    keywords: article
      ? `${article.tags.join(",")},看料88,今日猛料,热门大瓜,最新爆料合集`
      : "看料88,猛料合集",
    path: article ? `/material/${article.slug}` : "/materials",
    type: "article",
    image: article?.cover,
    robots: article ? "index,follow,max-image-preview:large" : "noindex,follow",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            image: article.cover,
            datePublished: article.publishedAt,
            dateModified: article.publishedAt,
            inLanguage: "zh-CN",
            articleSection: getCategoryName(article.categoryId),
            keywords: article.tags.join(","),
            author: { "@type": "Organization", name: "看料88 编辑部" },
            publisher: {
              "@type": "Organization",
              name: "看料88",
              url: SITE_ORIGIN,
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_ORIGIN}/material/${article.slug}` },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
              { "@type": "ListItem", position: 2, name: "猛料合集", item: `${SITE_ORIGIN}/materials` },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `${SITE_ORIGIN}/material/${article.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-24">
          <div className="text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-file-unknow-line text-3xl" />
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这条话题</h1>
            <p className="mt-2 text-sm text-foreground-600">链接可能已经失效，或者这条内容已被合并到其他合集。</p>
            <Link
              to="/materials"
              className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回猛料合集
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const categoryName = getCategoryName(article.categoryId);
  const related = getRelatedArticles(article, 3);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <article>
          <div className="border-b border-background-200 bg-background-100">
            <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-10">
              <Breadcrumbs
                items={[
                  { label: "首页", to: "/" },
                  { label: "猛料合集", to: "/materials" },
                  { label: categoryName, to: `/materials?cat=${article.categoryId.replace("cat-", "")}` },
                  { label: article.title },
                ]}
              />
              <span className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                <i className="ri-price-tag-3-line" />
                {categoryName}
              </span>
              <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug tracking-tight text-foreground-950 md:text-4xl">
                {article.title}
              </h1>
              <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {article.excerpt}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line" />
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" />
                  {article.publishedAt}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" />
                  {article.readTime} 分钟阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line" />
                  {article.heat.toLocaleString()} 热度
                </span>
              </div>
            </div>
          </div>

          <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-8 md:py-12">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-8">
                <div className="h-56 w-full overflow-hidden rounded-lg md:h-80">
                  <img
                    src={article.cover}
                    alt={`${article.title} 话题配图`}
                    title={`${article.title} - 看料88热点话题`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>

                <div className="mt-8 space-y-8">
                  {article.content.map((section, index) => (
                    <section key={section.heading} id={`section-${index + 1}`} className="scroll-mt-24">
                      <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                        {section.heading}
                      </h2>
                      <div className="mt-3 space-y-4">
                        {section.paragraphs.map((para, pIndex) => (
                          <p
                            key={`${section.heading}-${pIndex}`}
                            className="text-sm leading-7 text-foreground-700 md:text-base md:leading-8"
                          >
                            {para}
                          </p>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                  <span className="text-xs text-foreground-500">相关标签</span>
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/materials?q=${encodeURIComponent(tag)}`}
                      className="whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>

                <div className="mt-6 flex flex-col gap-3 rounded-lg bg-background-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs leading-relaxed text-foreground-600">
                    话题仍在更新中，后续进展会同步补充到本条合集里，建议收藏后回收查看。
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={copyLink}
                      className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-foreground-400"
                    >
                      <i className={copied ? "ri-check-line text-secondary-600" : "ri-link"} />
                      {copied ? "链接已复制" : "复制链接"}
                    </button>
                    <Link
                      to="/materials"
                      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-foreground-800"
                    >
                      <i className="ri-list-check-2" />
                      更多合集
                    </Link>
                  </div>
                </div>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-24">
                  <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                      <i className="ri-list-unordered text-primary-600" />
                      本条内容目录
                    </h2>
                    <ol className="mt-4 space-y-2.5">
                      {article.content.map((section, index) => (
                        <li key={section.heading}>
                          <a
                            href={`#section-${index + 1}`}
                            className="flex items-start gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-600"
                          >
                            <span className="mt-0.5 font-heading text-xs font-bold text-accent-600">
                              0{index + 1}
                            </span>
                            {section.heading}
                          </a>
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div className="mt-5 rounded-lg bg-foreground-950 p-5 text-background-200">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                      <i className="ri-mail-send-line text-lg" />
                    </span>
                    <h2 className="mt-4 font-heading text-base font-semibold text-background-50">
                      每周一封热点周报
                    </h2>
                    <p className="mt-2 text-xs leading-relaxed text-background-300">
                      把当周热度最高的十条话题和最新进展汇总到一封信里，不刷屏、不发广告。
                    </p>
                    <Link
                      to="/"
                      className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-xs font-semibold text-foreground-950 transition-colors hover:bg-accent-400"
                    >
                      回到首页订阅
                    </Link>
                  </div>

                  <div className="mt-5">
                    <h2 className="font-heading text-sm font-semibold text-foreground-950">相关话题推荐</h2>
                    <div className="mt-4 flex flex-col gap-4">
                      {related.map((item) => (
                        <ArticleCard
                          key={item.id}
                          article={item}
                          categoryName={getCategoryName(item.categoryId)}
                          variant="horizontal"
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}